import React, { useCallback, useEffect, useMemo, useState } from "react";
import DocumentPreviewPane from "../../common/DocumentPreview/DocumentPreviewPane";
import QmsModalPortal from "../qms/QmsModalPortal";
import { downloadQmsOriginalDocument } from "../qms/qmsDocumentDownloadUtils";
import "../qms/QmsPages.scss";
import { fetchStudyWorksheetOlderVersions } from "./scheduleOptionalSetupService";
import {
  buildWorksheetPreviewModel,
  getWorksheetFileExtension,
  getWorksheetUploadInfo,
} from "./scheduleWorksheetUtils";

function buildDocumentTitle(name, extension) {
  const base = String(name || "Document").trim() || "Document";
  const ext = String(extension || "")
    .trim()
    .replace(/^\./, "")
    .toLowerCase();
  if (!ext || base.toLowerCase().endsWith(`.${ext}`)) return base;
  return `${base}.${ext}`;
}

function ScheduleWorksheetPreviewModal({ open, onClose, ctx, initialRow, isAdhoc = false }) {
  const [activeRow, setActiveRow] = useState(null);
  const [olderVersions, setOlderVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  useEffect(() => {
    if (!open || !initialRow) {
      setActiveRow(null);
      setOlderVersions([]);
      return;
    }
    setActiveRow(initialRow);
  }, [open, initialRow]);

  const activePreview = useMemo(
    () => buildWorksheetPreviewModel(activeRow),
    [activeRow],
  );

  const versionRows = useMemo(() => {
    if (!initialRow) return olderVersions;
    const currentId =
      initialRow.StudyWorkSheetId || initialRow.studyWorkSheetId || null;
    const hasCurrent = olderVersions.some(
      (row) => (row.StudyWorkSheetId || row.studyWorkSheetId) === currentId,
    );
    const merged = hasCurrent ? [...olderVersions] : [initialRow, ...olderVersions];
    return merged.sort((a, b) => {
      const av = Number(a.VersionNumber ?? a.versionNumber ?? 0);
      const bv = Number(b.VersionNumber ?? b.versionNumber ?? 0);
      return bv - av;
    });
  }, [initialRow, olderVersions]);

  const showVersionList = !versionsLoading && versionRows.length > 1;

  const loadOlderVersions = useCallback(async () => {
    const groupId =
      initialRow?.StudyWorkSheetGroupId || initialRow?.studyWorkSheetGroupId;
    if (!groupId || !ctx?.studyScheduleId) {
      setOlderVersions([]);
      return;
    }
    setVersionsLoading(true);
    try {
      const rows = await fetchStudyWorksheetOlderVersions({
        ...ctx,
        visitId: isAdhoc ? null : ctx.visitId,
        studyWorkSheetGroupId: groupId,
      });
      setOlderVersions(Array.isArray(rows) ? rows : []);
    } catch {
      setOlderVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  }, [ctx, initialRow, isAdhoc]);

  useEffect(() => {
    if (!open || !initialRow) return;
    loadOlderVersions();
  }, [open, initialRow, loadOlderVersions]);

  const handleDownload = useCallback(() => {
    if (!activePreview?.documentUrl) return;
    const fileName = buildDocumentTitle(
      activePreview.documentTitle,
      activePreview.documentExtension,
    );
    downloadQmsOriginalDocument({
      documentUrl: activePreview.documentUrl,
      fileName,
    });
  }, [activePreview]);

  if (!open || !activePreview) return null;

  const uploadInfo = getWorksheetUploadInfo(activeRow);
  const currentId =
    activeRow?.StudyWorkSheetId || activeRow?.studyWorkSheetId || null;

  return (
    <QmsModalPortal>
      <div
        className="modal fade show d-block qms-doc-preview-modal gvisit-worksheet-preview"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Document preview"
        style={{ backgroundColor: "rgba(15, 23, 42, 0.45)", zIndex: 1200 }}
        onClick={onClose}
      >
        <div
          className="modal-dialog qms-doc-preview-modal__dialog"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="modal-content qms-doc-preview-modal__content">
            <div className="modal-header qms-doc-preview-modal__header gvisit-worksheet-preview__header">
              <div className="qms-doc-preview-modal__title-wrap">
                <h5 className="modal-title mb-0">
                  {activePreview.documentTitle || "Document"}
                </h5>
                <div
                  className="qms-doc-preview-modal__code"
                  title={uploadInfo.tooltip || undefined}
                >
                  Version {activePreview.version || 1}
                  {uploadInfo.shortText ? ` · ${uploadInfo.shortText}` : ""}
                </div>
              </div>
              <div className="gvisit-worksheet-preview__header-actions">
                {activePreview.documentUrl ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary qms-doc-preview-modal__download-btn"
                    onClick={handleDownload}
                    title="Download the original uploaded file"
                  >
                    <i className="fas fa-download me-1" aria-hidden="true" />
                    Download original
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close preview"
                  onClick={onClose}
                />
              </div>
            </div>

            <div className="modal-body qms-doc-preview-modal__body gvisit-worksheet-preview__body">
              <div className="gvisit-worksheet-preview__layout">
                <div className="gvisit-worksheet-preview__viewer">
                  {activePreview.documentUrl ? (
                    <DocumentPreviewPane
                      active
                      sourceModule="WORKSHEET"
                      sourceTable="StudyWorkSheet"
                      documentId={
                        activePreview.documentId ||
                        activePreview.studyWorkSheetId ||
                        ""
                      }
                      documentUrl={activePreview.documentUrl}
                      documentExtension={activePreview.documentExtension}
                      documentTitle={activePreview.documentTitle}
                      showDownloadOriginal={false}
                    />
                  ) : (
                    <div className="qms-doc-preview-modal__empty">
                      Document preview is not available for this file.
                    </div>
                  )}
                </div>

                {showVersionList ? (
                  <aside className="gvisit-worksheet-preview__versions">
                    <div className="gvisit-worksheet-preview__versions-head">
                      <h6>Older versions</h6>
                    </div>
                    <div className="gvisit-worksheet-preview__versions-body">
                      <ul className="gvisit-worksheet-preview__version-list">
                        {versionRows.map((row) => {
                          const rowId =
                            row.StudyWorkSheetId || row.studyWorkSheetId;
                          const version =
                            row.VersionNumber || row.versionNumber || "—";
                          const name =
                            row.WorkSheetName || row.workSheetName || "Document";
                          const ext = getWorksheetFileExtension(
                            name,
                            row.WorkSheetURL || row.workSheetUrl,
                          );
                          const isActive = rowId && rowId === currentId;
                          const rowInfo = getWorksheetUploadInfo(row);
                          return (
                            <li key={rowId || `${name}-${version}`}>
                              <button
                                type="button"
                                className={`gvisit-worksheet-preview__version-btn${
                                  isActive ? " is-active" : ""
                                }`}
                                title={rowInfo.tooltip || undefined}
                                onClick={() => setActiveRow(row)}
                              >
                                <span className="gvisit-worksheet-preview__version-title">
                                  v{version} · {name}
                                </span>
                                {rowInfo.shortText ? (
                                  <span className="gvisit-worksheet-preview__version-meta">
                                    {rowInfo.shortText}
                                  </span>
                                ) : null}
                                <span className="gvisit-worksheet-preview__version-ext">
                                  {ext.toUpperCase()}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </aside>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </QmsModalPortal>
  );
}

export default ScheduleWorksheetPreviewModal;
